import { useOpenStreetGeoDataAPI } from "@/app/hooks/api_hooks/geo/useOpenStreetGeoDataAPI";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useFormUtils } from "@/app/hooks/useFormUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Fragment, useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import FormFieldErrors from "../../Reusable/FormFieldErrors";
import { IGeoSearchSelectMenu, ISelectMenuItemData } from "../../interface";
import { IGeoAddressState } from "@/api_framework/api_modals/GeoAddresses";
import { ICustomerProfileState, setCustomerAddresses } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";

const isValidSearchQuery = (query: string): boolean => {
  if (!query?.trim()) return false;
  const addressRegex = /^[a-zA-Z0-9\s.,\-'#/&()]+$/;
  return addressRegex.test(query.trim());
};

const GeoSearchSelectMenu: React.FC<IGeoSearchSelectMenu> = ({
  errorMessages,
  onSelectItem,
  className,
  itemsClassName,
  register,
  fieldError,
  listBoxClassName,
  showTypedErrors,
  showDropdownWhenNoData,
  showTooltips,
  ...props
}) => {
  const { deduceFormFieldErrors, isFormFieldValid } = useFormUtils();
  const [preFilledFormattedAddress, setPrefilledFormattedAddress] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<ISelectMenuItemData | null>(null);
  const [query, setQuery] = useState("");
  const hasPrefilledRef = useRef(false);
  const hasUserEditedQueryRef = useRef(false);
  const { geoAddress, execute: searchLocation, clearGeoSearch } = useOpenStreetGeoDataAPI();
  const isGeoLocationSearchInProgress = useLoader("isGeoLocationSearchInProgress");
  const debouncedSearch = useDebounce(query, 400);

  const { getRouteKey } = useRouter();
  const routeIdCustomerProfile = getRouteKey("CUSTOMER_PROFILE", "id");
  const { routeState: customerProfileRouteState } = useRouteInfo(routeIdCustomerProfile);
  const { customerAddresses } = customerProfileRouteState.state as ICustomerProfileState;

  const { routeState } = useRouteInfo(getRouteKey("SLOTS", "id"));
  const { isPageReadonly } = routeState.state as ISlotsState;

  const [params, _setParams] = useSearchParams();
  const rescheduleBookingId = params?.get("rbi");

  const dispatch = useAppDispatch();

  useAfterFirstRender(() => {
    if (!hasUserEditedQueryRef.current) return;
    const nextQuery = debouncedSearch?.trim();
    if (!nextQuery || !isValidSearchQuery(nextQuery)) {
      clearGeoSearch();
      return;
    }
    searchLocation(nextQuery);
  }, [debouncedSearch]);


  useEffect(() => {
    if (isPageReadonly) {
      dispatch(
        setCustomerAddresses({
          addresses: [],
        }),
      );
    }

  }, [isPageReadonly])

  useEffect(() => {
    if (rescheduleBookingId) return;
    if (props?.skipDefaultPrefill) return;
    if (hasPrefilledRef.current) return;
    const nextQuery = customerAddresses?.[0]?.address_2 ?? "";
    if (!nextQuery) return;
    setPrefilledFormattedAddress(nextQuery);
    hasPrefilledRef.current = true;
  }, [rescheduleBookingId, customerAddresses, props?.skipDefaultPrefill]);

  const filteredGeoData = useSelectMenuReducer(geoAddress, "formattedAddress", "placeId");

  const geoAddressByPlaceId = geoAddress?.reduce(
    (acc, ga) => {
      acc[ga.placeId] = ga;
      return acc;
    },
    {} as Record<string, IGeoAddressState>,
  );

  useEffect(() => {
    if (rescheduleBookingId) return;
    if (props?.skipDefaultPrefill) return;
    if (hasUserEditedQueryRef.current) return;
    if (!customerAddresses?.length || !filteredGeoData?.length) return;
    const address = filteredGeoData[0];
    if (!address || selectedAddress?.id === address.id) return;
    setSelectedAddress(address);
  }, [filteredGeoData, customerAddresses, rescheduleBookingId, selectedAddress?.id, props?.skipDefaultPrefill]);

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);

  const isValid = isFormFieldValid(reducedErrorMessages);

  const showFieldErrors = showTypedErrors && showTypedErrors === true ? true : showTypedErrors === false ? false : true;

  const handleAddressItemSelect = (item: ISelectMenuItemData) => {
    setSelectedAddress(item);
    if (onSelectItem) {
      const selectedAddress = geoAddress?.filter((ga) => ga.placeId === item.id);
      onSelectItem({
        ...item,
        state: selectedAddress[0] ?? {},
      });
    }
  };

  const resetInput = () => {
    setSelectedAddress(null);
    setQuery("");
    clearGeoSearch();
    if (props?.onClear) {
      props?.onClear();
    }
    setPrefilledFormattedAddress(null);
  };

  useEffect(() => {
    if (props?.clearSearch === true) {
      setSelectedAddress(null);
      setQuery("");
      setPrefilledFormattedAddress(null);
      clearGeoSearch();
    }
  }, [props?.clearSearch]);

  useEffect(() => {
    if (props?.defaultAddressString && props?.defaultAddressString !== "") {
      setPrefilledFormattedAddress(props?.defaultAddressString);
    }
  }, [props?.defaultAddressString]);

  return (
    <div style={props?.style ?? {}} className={clsx("relative ", props?.containerClassName ?? "")}>
      {/* <input type="hidden" {...register} /> */}
      <Combobox
        disabled={props?.disabled && props?.disabled === true ? true : false}
        value={selectedAddress}
        onChange={(item: ISelectMenuItemData) => {
          handleAddressItemSelect(item);
        }}
        nullable
      >
        {props?.label && (
          <span className="-top-5 left-1 absolute flex justify-start items-center text-gray-500 text-xs">
            {props?.label}{" "}
            {props?.labelInfo && (
              <span
                data-tooltip-content={props?.labelInfo}
                data-tooltip-id={`${register.name}:label_info`}
                className="ml-1"
              >
                {" "}
                <InformationCircleIcon className="inline w-4 h-4" />{" "}
                <Tooltip id={`${register.name}:label_info`} style={{ fontSize: "10px" }} />
              </span>
            )}
          </span>
        )}
        <div className="relative">
          <div
            className={clsx(
              "relative flex items-center bg-white border rounded-lg text-left sm:text-sm overflow-hidden transition-colors focus-within:ring-2 focus-within:ring-gray-800/10",
              isValid
                ? "border-gray-300 hover:border-gray-400 focus-within:border-gray-800"
                : "border-red-500 focus-within:border-red-500 focus-within:ring-red-500/10",
              props?.disabled && props?.disabled === true && "bg-gray-200",
            )}
          >
            <MagnifyingGlassIcon className="left-3 z-10 absolute w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" />
            <Combobox.Input
              className={clsx(
                `w-full text-left text-xs font-medium rounded-lg py-2.5 pl-9 pr-9 bg-transparent placeholder:text-gray-400 focus:ring-0 ${className}`,
                isValid
                  ? "text-gray-700 border-none outline-none font-medium"
                  : "text-red-600 border-none outline-none font-medium",
                props?.disabled && props?.disabled === true && "bg-gray-200",
              )}
              displayValue={(item: any) =>
                item ? item?.title : preFilledFormattedAddress ? preFilledFormattedAddress : ""
              }
              placeholder={props?.placeholder}
              onChange={(event) => {
                hasUserEditedQueryRef.current = true;
                setPrefilledFormattedAddress(null);
                setQuery(event.target.value);
              }}
              autoComplete="off"
              autoCorrect="false"
            />
            {((props?.withClearAction && selectedAddress) || preFilledFormattedAddress) && (
              <span
                onClick={() => {
                  resetInput();
                }}
                className="right-2 absolute inset-y-0 flex items-center p-0.5 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5 text-red-500 hover:text-red-600 transition-colors" />
              </span>
            )}
          </div>
          {showFieldErrors && (
            <div className="">
              <FormFieldErrors errors={reducedErrorMessages} />
            </div>
          )}
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <div className="absolute border-gray-50 bg-white shadow-md mt-2 border rounded-md w-full max-h-60">
              <Combobox.Options
                style={{ zIndex: 1 }}
                className={clsx(
                  filteredGeoData?.length > 0 &&
                  "absolute  mt-1  overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none max-h-60 sm:text-sm",
                  listBoxClassName ? listBoxClassName : "w-full",
                )}
              >
                {isGeoLocationSearchInProgress && (
                  <div className="z-20 border-gray-100 bg-white p-2 border text-center text-gray-400 text-xs">
                    Searching...
                  </div>
                )}
                {filteredGeoData?.length === 0
                  ? showDropdownWhenNoData &&
                  showDropdownWhenNoData === true &&
                  query?.length > 0 &&
                  !isGeoLocationSearchInProgress && (
                    <div className="relative z-20 flex flex-col items-center gap-1 border border-gray-100 bg-white px-3 py-3 text-center cursor-default select-none">
                      <span className="flex justify-center items-center bg-red-50 rounded-full w-7 h-7">
                        <MapPinIcon className="w-4 h-4 text-red-400" />
                      </span>
                      <p className="font-semibold text-gray-700 text-[11px]">No locations found</p>
                      <p className="max-w-[220px] text-gray-400 text-[10px] leading-tight">
                        Try a different area or landmark.
                      </p>
                    </div>
                  )
                  : filteredGeoData?.map((item: ISelectMenuItemData) => (
                    <Combobox.Option
                      key={item.id}
                      className={({ active }) =>
                        clsx(
                          "relative flex font-medium  px-2 text-xs justify-start items-center cursor-pointer select-none py-2 text-left border-b border-gray-100 last:border-b-0 ",
                          selectedAddress?.id === item.id
                            ? "bg-gray-300 text-gray-900 font-medium "
                            : active
                              ? "bg-gray-200 text-gray-900"
                              : "text-gray-800 hover:bg-gray-200 hover:text-gray-900",
                          itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                        )
                      }
                      onClick={() => setSelectedAddress(item)}
                      data-tooltip-place="right"
                      data-tooltip-id={`option-tooltip:${register?.name}`}
                      data-tooltip-content={item.title}
                      value={item}
                    >
                      {({ selected: _l, active }) => {
                        const geoItem = geoAddressByPlaceId?.[item.id];
                        const name = geoItem?.name;
                        const formattedAddress = geoItem?.formattedAddress ?? item.title;
                        return (
                          <>
                            <span className="shrink-0">
                              <MapPinIcon className="w-4 h-4 text-red-500" />
                            </span>
                            <div className="flex flex-col flex-1 ml-2 text-left min-w-0">
                              {name && <span className="font-semibold text-xs">{name}</span>}
                              <p className="mt-0.5 text-[11px] leading-tight break-words opacity-90">
                                {formattedAddress}
                              </p>
                            </div>
                            {selectedAddress?.id === item?.id ? (
                              <div className="flex justify-start items-center text-left">
                                <span
                                  className={`inset-y-0 text-left left-0 flex items-center pl-3 ${active ? "text-white" : "text-blue-600"
                                    }`}
                                ></span>
                                <CheckIcon className="mt-0.5 shrink-0 w-4 h-4" />
                              </div>
                            ) : null}
                          </>
                        );
                      }}
                    </Combobox.Option>
                  ))}
              </Combobox.Options>
            </div>
          </Transition>
        </div>
      </Combobox>
      {showTooltips && showTooltips === true && (
        <Tooltip id={`option-tooltip:${register?.name}`} style={{ zIndex: 5, fontSize: "10px" }} />
      )}
    </div>
  );
};

export default GeoSearchSelectMenu;
